import { useCallback, useReducer, useRef } from "react";
import { OPENER, pickReply } from "~/lib/ai/cannedReplies";

export type ChatRole = "user" | "assistant";

export interface ChatMessage {
  id: string;
  role: ChatRole;
  content: string;
  streaming?: boolean;
}

interface ChatState {
  messages: ChatMessage[];
  isStreaming: boolean;
}

type ChatAction =
  | { type: "USER_SEND"; userMsg: ChatMessage; placeholder: ChatMessage }
  | { type: "APPEND_TOKEN"; id: string; token: string }
  | { type: "FINISH_STREAM"; id: string }
  | { type: "RESET" };

const INITIAL_STATE: ChatState = {
  messages: [{ id: "opener", role: "assistant", content: OPENER }],
  isStreaming: false,
};

function reducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case "USER_SEND":
      return {
        ...state,
        messages: [...state.messages, action.userMsg, action.placeholder],
        isStreaming: true,
      };
    case "APPEND_TOKEN":
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === action.id ? { ...m, content: m.content + action.token } : m,
        ),
      };
    case "FINISH_STREAM":
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === action.id ? { ...m, streaming: false } : m,
        ),
        isStreaming: false,
      };
    case "RESET":
      return INITIAL_STATE;
  }
}

export type AIChatMode = "canned" | "live";

interface UseAIChatOptions {
  mode?: AIChatMode;
  /** Average ms per token when streaming canned replies. Default 38ms. */
  tokenIntervalMs?: number;
}

interface UseAIChatResult {
  messages: ChatMessage[];
  isStreaming: boolean;
  send: (text: string) => void;
  reset: () => void;
}

function uid(): string {
  return Math.random().toString(36).slice(2, 10);
}

function tokenize(text: string): string[] {
  // Split into words + whitespace + punctuation chunks so the stream looks
  // like real LLM streaming (boundary-aligned, not character-by-character).
  return text.match(/\S+\s*|\s+/g) ?? [text];
}

export function useAIChat(options: UseAIChatOptions = {}): UseAIChatResult {
  const { mode = "canned", tokenIntervalMs = 38 } = options;
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const abortRef = useRef<(() => void) | null>(null);

  const send = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || state.isStreaming) return;

      const userMsg: ChatMessage = {
        id: uid(),
        role: "user",
        content: trimmed,
      };
      const assistantId = uid();
      const placeholder: ChatMessage = {
        id: assistantId,
        role: "assistant",
        content: "",
        streaming: true,
      };
      dispatch({ type: "USER_SEND", userMsg, placeholder });

      if (mode === "canned") {
        streamCanned(trimmed, assistantId, tokenIntervalMs, dispatch, abortRef);
      } else {
        streamLive(trimmed, assistantId, dispatch, abortRef);
      }
    },
    [mode, state.isStreaming, tokenIntervalMs],
  );

  const reset = useCallback(() => {
    abortRef.current?.();
    abortRef.current = null;
    dispatch({ type: "RESET" });
  }, []);

  return {
    messages: state.messages,
    isStreaming: state.isStreaming,
    send,
    reset,
  };
}

function streamCanned(
  userText: string,
  assistantId: string,
  intervalMs: number,
  dispatch: React.Dispatch<ChatAction>,
  abortRef: React.MutableRefObject<(() => void) | null>,
): void {
  const reply = pickReply(userText);
  const tokens = tokenize(reply);
  let i = 0;
  let cancelled = false;

  // Tiny "thinking" delay so it feels less instant.
  const startTimer = window.setTimeout(tick, 250);

  function tick(): void {
    if (cancelled) return;
    if (i >= tokens.length) {
      dispatch({ type: "FINISH_STREAM", id: assistantId });
      abortRef.current = null;
      return;
    }
    const token = tokens[i];
    if (token !== undefined) {
      dispatch({ type: "APPEND_TOKEN", id: assistantId, token });
    }
    i += 1;
    // Slight jitter so it doesn't feel mechanical.
    const jitter = 0.7 + Math.random() * 0.6;
    window.setTimeout(tick, intervalMs * jitter);
  }

  abortRef.current = () => {
    cancelled = true;
    window.clearTimeout(startTimer);
    dispatch({ type: "FINISH_STREAM", id: assistantId });
  };
}

async function streamLive(
  userText: string,
  assistantId: string,
  dispatch: React.Dispatch<ChatAction>,
  abortRef: React.MutableRefObject<(() => void) | null>,
): Promise<void> {
  // Live mode: POSTs to /api/ai/chat, expects an SSE-style streaming
  // response. The Hono server doesn't expose this endpoint today (would
  // need an ANTHROPIC_API_KEY + a Hono route to stream Messages API
  // responses through). Flipping the mode flag in useAIChat is the only
  // client-side change needed to wire it up.
  const controller = new AbortController();
  abortRef.current = () => controller.abort();

  try {
    const res = await fetch("/api/ai/chat", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ message: userText }),
      signal: controller.signal,
    });
    if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      if (chunk) {
        dispatch({ type: "APPEND_TOKEN", id: assistantId, token: chunk });
      }
    }
  } catch {
    dispatch({
      type: "APPEND_TOKEN",
      id: assistantId,
      token:
        "(live LLM mode isn't wired on this site — it's intentionally canned. Switch back to canned mode to see the demo.)",
    });
  } finally {
    dispatch({ type: "FINISH_STREAM", id: assistantId });
    abortRef.current = null;
  }
}
